import { useState, useMemo, useEffect } from 'react';

/**
 * 加入時期（日付範囲）のエラー状態を管理するカスタムフック
 *
 * - onHasError が渡された場合のみ有効化（opt-in）
 * - 開始日・終了日それぞれのフィールドエラーを管理する
 * - 両フィールドが有効な場合のみ範囲エラー（開始日 > 終了日）を計算する
 * - エラー有無を onHasError コールバックで親に通知する
 *
 * @param {string} dateFrom - 加入時期開始（YYYY/MM/DD）
 * @param {string} dateTo - 加入時期終了（YYYY/MM/DD）
 * @param {Function|undefined} onHasError - エラー有無を受け取るコールバック（省略可）
 * @returns {{ setFromError: Function, setToError: Function, displayError: string|null }}
 */
const useDateRangeError = (dateFrom, dateTo, onHasError) => {
  const [fromError, setFromError] = useState(null);
  const [toError, setToError] = useState(null);

  // フィールドエラーがない場合のみ範囲エラーを計算する
  const rangeError = useMemo(() => {
    if (fromError || toError || !dateFrom || !dateTo) return null;
    return dateFrom.replace(/\//g, '-') > dateTo.replace(/\//g, '-')
      ? '加入時期の開始日は終了日以前の日付を選択してください。'
      : null;
  }, [dateFrom, dateTo, fromError, toError]);

  // エラー有無を親に通知する
  useEffect(() => {
    onHasError?.(!!fromError || !!toError || !!rangeError);
  }, [fromError, toError, rangeError, onHasError]);

  return {
    setFromError,
    setToError,
    displayError: fromError || toError || rangeError,
  };
};

export default useDateRangeError;
